/**
 * useResidents Hook
 * CCH Care — Phase 2
 * Last Modified: 2026-04-22
 *
 * Manages assigned resident list with filtering, sorting, and search.
 *
 * Changes (2026-04-22) — Float-view support:
 * - fetchResidents no longer early-returns when shift_assignment_id is
 *   missing IF shift.is_float_view is true. Care Admins and Supervisors
 *   overseeing a shift without being personally rostered don't have a
 *   direct assignment row; the backend accepts a null assignment_id
 *   and broadens the query to every resident on the care_shift at the
 *   user's facility (role-gated). See api/residents/assigned.php.
 * - The API call passes null for assignment_id in float mode. axios
 *   with { params: { shift_assignment_id: null } } omits the key, which
 *   the backend treats as float mode. (The backend also defends against
 *   0, empty string, and the literal string "null" — so this works
 *   regardless of how patients.js serializes the URL.)
 * - Dep array gains shift.is_float_view so the hook re-runs if the
 *   shift's shape flips (e.g. admin gets assigned mid-shift, or a CNA's
 *   assignment is removed and they become float-eligible).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAssignedResidents } from '../api/patients';

export default function useResidents(shift) {
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter/sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: [],      // ['overdue', 'in_progress', 'complete', 'not_started']
    alertType: [],   // ['fall_risk', 'dnr', 'diet_restriction', ...]
    newAdmitsOnly: false,
  });
  const [sortBy, setSortBy] = useState('urgency'); // urgency | room | adl_percent
  const [viewMode, setViewMode] = useState('list'); // list | grid

  // Fetch residents
  //
  // Gate:
  //   - care_shift_id is always required (no shift context → nothing to load)
  //   - shift_assignment_id is required ONLY for non-float shifts. Float-view
  //     shifts (Care Admin/Supervisor oversight) legitimately have a null
  //     assignment id and the backend handles that by broadening scope to
  //     care_shift + facility.
  const fetchResidents = useCallback(async () => {
    if (!shift?.care_shift_id) return;
    const isFloat = !!shift?.is_float_view;
    if (!isFloat && !shift.shift_assignment_id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getAssignedResidents(
        isFloat ? null : shift.shift_assignment_id,
        shift.care_shift_id
      );
      if (data.success) {
        setResidents(data.residents || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load residents');
    } finally {
      setLoading(false);
    }
  }, [shift?.shift_assignment_id, shift?.care_shift_id, shift?.is_float_view]);

  useEffect(() => {
    fetchResidents();
  }, [fetchResidents]);

  // Auto-refresh every 3 minutes
  useEffect(() => {
    if (!shift) return;
    const interval = setInterval(fetchResidents, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [shift, fetchResidents]);

  // Filtered + sorted residents
  const filteredResidents = useMemo(() => {
    let list = [...residents];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r =>
        r.full_name.toLowerCase().includes(q) ||
        (r.room_number && r.room_number.toLowerCase().includes(q))
      );
    }

    // Status filter
    if (filters.status.length > 0) {
      list = list.filter(r => filters.status.includes(r.status));
    }

    // Alert type filter
    if (filters.alertType.length > 0) {
      list = list.filter(r =>
        r.alerts?.some(a => filters.alertType.includes(a.alert_type))
      );
    }

    // New admits only
    if (filters.newAdmitsOnly) {
      list = list.filter(r => r.is_new_admit);
    }

    // Sort
    list.sort((a, b) => {
      switch (sortBy) {
        case 'urgency': {
          const priorityOrder = { overdue: 0, not_started: 1, in_progress: 2, complete: 3 };
          const pa = priorityOrder[a.status] ?? 2;
          const pb = priorityOrder[b.status] ?? 2;
          if (pa !== pb) return pa - pb;
          return (a.room_number || '').localeCompare(b.room_number || '');
        }
        case 'room':
          return (a.room_number || '').localeCompare(b.room_number || '', undefined, { numeric: true });
        case 'adl_percent':
          return a.adl_percent - b.adl_percent;
        default:
          return 0;
      }
    });

    return list;
  }, [residents, searchQuery, filters, sortBy]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.alertType.length > 0) count++;
    if (filters.newAdmitsOnly) count++;
    return count;
  }, [filters]);

  // Toggle a filter value within a key
  const toggleFilter = useCallback((key, value) => {
    setFilters(prev => {
      if (key === 'newAdmitsOnly') {
        return { ...prev, newAdmitsOnly: !prev.newAdmitsOnly };
      }
      const current = prev[key] || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ status: [], alertType: [], newAdmitsOnly: false });
    setSearchQuery('');
  }, []);

  return {
    residents: filteredResidents,
    allResidents: residents,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    toggleFilter,
    clearFilters,
    activeFilterCount,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    refresh: fetchResidents,
  };
}